import React, { memo, useRef, useEffect } from 'react';

import { useSidePanel } from '@/app/store/ui';
import { useWidgetPanel } from '@/app/store/widget';
import { useUIContextStore } from '@/app/store/ui-context';
import { getWidgetComponentSync } from '@/lib/config/agents.config';
import { logger } from '@/lib/utils/logger';

import { SidePanelHeader } from '../SidePanelHeader';
import { SidePanelScrollProvider, SidePanelScrollContainer } from '../SidePanelScrollContext';
import type { WidgetPanelProps } from './SidePanel.types';
import { useTranslation } from 'react-i18next';

/**
 * Registers a fallback ref so the agent can still extract panel content from a widget
 * that never sets its own UI context. A widget may override it with `setWidgetRef`, or
 * set a description via `setContext`, which `getContextSnapshot` prefers over the ref.
 *
 * Child effects run before parent effects, so a widget that claims the ref or the context
 * in its own effect wins: this wrapper only clears on unmount when the stored ref is
 * still the one it set.
 */
function WidgetContentWrapper({ children }: { children: React.ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const store = useUIContextStore.getState();
    store.setWidgetRef(contentRef);
    // Clean up on unmount - but only clear if we set the ref
    return () => {
      const currentStore = useUIContextStore.getState();
      // Only clear if the current ref is our ref (not overwritten by widget)
      if (currentStore.widgetRef === contentRef) {
        currentStore.clearContext();
      }
    };
  }, []);

  return (
    <div ref={contentRef} className="h-full">
      {children}
    </div>
  );
}

interface StoredWidgetProps {
  component: React.ComponentType<{ data: unknown }>;
  data: unknown;
}

/**
 * Re-renders a stored widget only when its data changes. Without it, every update around
 * the panel (a layout-mode toggle, a parent re-render) re-renders the whole widget tree.
 */
const StoredWidget = memo(function StoredWidget(props: StoredWidgetProps) {
  const Component = props.component;
  return <Component data={props.data} />;
});

/**
 * WidgetPanel - Default panel for displaying widget content
 * Automatically loads widget from store or renders custom children
 */
function WidgetPanel(props: WidgetPanelProps) {
  const { t } = useTranslation('common');

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { headerConfig, title, onClose, headerAction, header, children, scrollKey } = props;
  const { closeSidePanel } = useSidePanel();
  const { widget, clearWidget } = useWidgetPanel();

  const handleClose =
    onClose &&
    (() => {
      clearWidget();
      closeSidePanel();
      onClose();
    });

  // Determine what content to show
  let content: React.ReactNode = null;
  const panelTitle = title;

  if (children) {
    // Custom children provided
    content = children;
  } else if (widget) {
    // Load widget from store
    const viewAgentId = widget.sourceAgentId ?? widget.agentName;
    const WidgetComponent = viewAgentId ? getWidgetComponentSync(viewAgentId, widget.key) : null;

    if (!WidgetComponent) {
      content = (
        <div className="h-full flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <p>{t('components.sidePanel.widgetComponentNotFound')}</p>
            <p className="text-sm mt-2">
              {t('components.sidePanel.agent', {
                agentName: viewAgentId || t('components.sidePanel.unknown'),
              })}
            </p>
          </div>
        </div>
      );
    } else if (!widget.data) {
      content = (
        <div className="h-full flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <p>{t('components.sidePanel.noWidgetDataAvailable')}</p>
            <p className="text-sm mt-2">
              {t('components.sidePanel.agent', {
                agentName: viewAgentId || t('components.sidePanel.unknown'),
              })}
            </p>
          </div>
        </div>
      );
    } else {
      try {
        const TypedWidgetComponent = WidgetComponent as React.ComponentType<{
          data: unknown;
        }>;
        // Render widget directly - it will provide its own wrapper if needed

        content = <StoredWidget component={TypedWidgetComponent} data={widget.data} />;
      } catch (error) {
        logger.error('Error rendering widget:', error);
        content = (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-destructive">
              <p>{t('components.sidePanel.widgetRenderingError')}</p>
              <p className="text-sm mt-2">
                {t('components.sidePanel.agent', {
                  agentName: viewAgentId || t('components.sidePanel.unknown'),
                })}
              </p>
              <p className="text-xs mt-1">
                {t('components.sidePanel.error', {
                  message:
                    error instanceof Error ? error.message : t('components.sidePanel.unknownError'),
                })}
              </p>
            </div>
          </div>
        );
      }
    }
  }

  // A widget loaded from the store renders without the header and surface below: it brings
  // its own chrome.
  if (widget && !children) {
    return (
      <SidePanelScrollProvider>
        <WidgetContentWrapper>{content}</WidgetContentWrapper>
      </SidePanelScrollProvider>
    );
  }

  // eslint-disable-next-line i18next/no-literal-string -- scroll-position cache key
  const effectiveScrollKey = scrollKey ?? widget?.key ?? 'default';

  return (
    <div className="h-full flex flex-col overflow-y-auto rounded-lg bg-background">
      {/* Custom header (complete override) */}
      {header ? (
        header
      ) : (
        /* New configurable header */
        <SidePanelHeader
          title={panelTitle}
          onClose={handleClose}
          // eslint-disable-next-line @typescript-eslint/no-deprecated -- Backward compatibility
          headerAction={headerAction}
          {...headerConfig}
        />
      )}

      {/* Content */}
      <SidePanelScrollContainer scrollKey={effectiveScrollKey}>{content}</SidePanelScrollContainer>
    </div>
  );
}

/**
 * SidePanel - Compound component for side panels
 */
export const SidePanel = Object.assign(WidgetPanel, {
  Widget: WidgetPanel, // Alias for consistency
});
